import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  CircularProgress,
  TablePagination,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import SetupPracticeReviewDialog from '../components/setup-practice/SetupPracticeReviewDialog';

const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';

export default function SetupPracticeRequests() {
  const location = useLocation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const { tokens } = useContext(AuthContext);
  const [autoOpenedReview, setAutoOpenedReview] = useState(false);

  const handleReview = (request) => {
    setSelectedRequest(request);
    setReviewDialogOpen(true);
  };

  // Handle approval with tax exemption and in-house collection flag
  const handleApprove = async (
    isTaxExempt = false,
    isDoingInHouseCollection = false,
    qbId = ''
  ) => {
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('is_tax_exempt', String(isTaxExempt));
      // Do not mark as collection site at this step; instead capture in-house collection intent
      params.set('is_doing_in_house_collection', String(isDoingInHouseCollection));
      if (qbId !== undefined && qbId !== null && qbId !== '') {
        params.set('qb_id', qbId);
      }
      const response = await fetch(
        `${apiBase}/setup-practice-requests/${selectedRequest.id}/approve?${params.toString()}`,
        {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${tokens.id_token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to approve request');
      }

      // Refresh the requests list
      await fetchRequests();
      setReviewDialogOpen(false);
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${apiBase}/setup-practice-requests/${selectedRequest.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${tokens.id_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...selectedRequest,
          status: 'Cancelled'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel request');
      }

      // Refresh the requests list
      await fetchRequests();
      setReviewDialogOpen(false);
    } catch (error) {
      console.error('Error cancelling request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${apiBase}/setup-practice-requests/${selectedRequest.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${tokens.id_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...selectedRequest,
          status: 'Submitted'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to re-submit request');
      }

      // Refresh the requests list
      await fetchRequests();
      setResubmitDialogOpen(false);
    } catch (error) {
      console.error('Error re-submitting request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
    setPage(0);
  };

  const fetchRequests = async () => {
    try {
      const params = new URLSearchParams();
      params.append('skip', page * rowsPerPage);
      params.append('limit', rowsPerPage);
      if (selectedStatus && selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      const response = await fetch(`${apiBase}/setup-practice-requests/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${tokens.id_token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch setup practice requests');
      }
      const data = await response.json();
      setRequests(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching setup practice requests:', error);
    } finally {
      setLoading(false);
      // Notify badge count to refresh
      if (window && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new Event('sprCountChanged'));
      }
    }
  };

  // Support deep link: /setup-practice-requests?review=<request_id>
  useEffect(() => {
    if (!loading && !reviewDialogOpen && !autoOpenedReview) {
      const params = new URLSearchParams(location.search || '');
      const reviewId = params.get('review');
      if (reviewId && Array.isArray(requests) && requests.length > 0) {
        const match = requests.find(r => String(r.id) === String(reviewId));
        if (match) {
          setSelectedRequest(match);
          setReviewDialogOpen(true);
          setAutoOpenedReview(true);
        }
      }
    }
  }, [location.search, loading, reviewDialogOpen, autoOpenedReview, requests]);

  useEffect(() => {
    if (!tokens?.id_token) return;
    setLoading(true);
    fetchRequests();
  }, [tokens, page, rowsPerPage, selectedStatus]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Setup practice requests
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={selectedStatus}
            label="Status"
            onChange={handleStatusChange}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="Unsubmitted">Unsubmitted</MenuItem>
            <MenuItem value="Submitted">Submitted</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
            <MenuItem value="Verified">Verified</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Practice name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Request date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests?.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{`${request.provider_name_prefix || ''} ${request.provider_first_name || ''} ${request.provider_middle_name || ''} ${request.provider_last_name || ''} ${request.provider_name_suffix || ''}`.trim()}</TableCell>
                <TableCell>{request.practice_email}</TableCell>
                <TableCell>{request.practice_name}</TableCell>
                <TableCell>{request.status}</TableCell>
                <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  {request.status === 'Submitted' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleReview(request)}
                    >
                      Review
                    </Button>
                  )}
                  {request.status === 'Cancelled' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSelectedRequest(request);
                        setResubmitDialogOpen(true);
                      }}
                    >
                      Re-Submit
                      </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <SetupPracticeReviewDialog
        open={reviewDialogOpen}
        onClose={() => {
          setReviewDialogOpen(false);
          // Clear deep-link param on close for cleaner URL
          const params = new URLSearchParams(location.search || '');
          if (params.has('review')) {
            params.delete('review');
            navigate({ pathname: '/setup-practice-requests', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
          }
        }}
        request={selectedRequest}
        onApprove={handleApprove}
        onCancel={handleCancel}
        loading={actionLoading}
      />

      <Dialog
        open={resubmitDialogOpen}
        onClose={() => setResubmitDialogOpen(false)}
      >
        <DialogTitle>Re-Submit Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to re-submit this request? This will change the status back to Submitted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResubmitDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleResubmit} color="primary" disabled={actionLoading}>
            {actionLoading ? 'Re-Submitting...' : 'Re-Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 
